import React, { useEffect, useRef, useState } from 'react';
import './ConversationDisplay.scss';
import CHARACTERS from '../../data/characters';
import { getPlayer } from '../../services/audioPlayer';

const CHARACTER_MAP = {};
CHARACTERS.forEach((c) => {
  CHARACTER_MAP[c.id] = { displayName: c.displayName, color: c.placeholderColor };
});

const LINE_GAP_MS = 600;
const LINE_START_DELAY_MS = 300;

function splitIntoSegments(text) {
  if (text.length <= 15) return [text];

  const result = [];
  let current = '';

  for (const char of text) {
    current += char;
    if (current.length >= 8 && '、。！？…～'.includes(char)) {
      result.push(current);
      current = '';
    }
  }
  if (current) {
    if (result.length > 0 && current.length < 5) {
      result[result.length - 1] += current;
    } else {
      result.push(current);
    }
  }

  return result.length > 0 ? result : [text];
}

const ConversationDisplay = ({ lines, activeLineIndex, onLineComplete, audioMap, paused = false, muted = false }) => {
  const gapTimerRef = useRef(null);
  const segTimerRef = useRef(null);
  const startTimerRef = useRef(null);
  // いま共有プレイヤーに読み込んである行ID。
  // 同じ行で再描画が起きても src を入れ直さない（＝同じセリフの二重再生を防ぐ）
  const loadedIdRef = useRef(null);
  // 共有プレイヤーに付けたリスナーを外すための関数
  const detachRef = useRef(null);
  // いま再生対象の行ID。古い行のイベントハンドラはこれを見て無視する
  const currentIdRef = useRef(null);
  // 音声なしで進める（フォールバック）を開始済みの行ID
  const fallbackStartedRef = useRef(new Set());
  // 一時停止の制御用。
  //   gapStateRef      … 次の行へ進むタイマーの締切（再開時に残り時間で張り直す）
  //   pendingStartRef  … 一時停止中に再生開始を見送った行
  //   fallbackFnRef    … 音声なしで進める処理（再開時に呼び直す）
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const gapStateRef = useRef(null);
  const pendingStartRef = useRef(null);
  const fallbackFnRef = useRef(null);

  const [segmentIndex, setSegmentIndex] = useState(0);
  const [ready, setReady] = useState(false);
  const [prevLineIndex, setPrevLineIndex] = useState(-1);

  const onLineCompleteRef = useRef(onLineComplete);
  onLineCompleteRef.current = onLineComplete;
  const audioMapRef = useRef(audioMap);
  audioMapRef.current = audioMap;

  if (activeLineIndex !== prevLineIndex) {
    setPrevLineIndex(activeLineIndex);
    setSegmentIndex(0);
    // 同じ行に戻ってきた（待機を挟んだ）場合は、再生中の続きを見せたいので ready は落とさない
    if (activeLineIndex < 0) setReady(false);
  }

  const currentLine = activeLineIndex >= 0 && activeLineIndex < lines.length
    ? lines[activeLineIndex]
    : null;
  const segments = currentLine ? splitIntoSegments(currentLine.text) : [];

  // 今より前の行にこのキャラが登場していなければ初回
  const isFirstAppearance = currentLine
    && !lines.slice(0, activeLineIndex).some((l) => l.speaker === currentLine.speaker);

  useEffect(() => {
    if (activeLineIndex < 0 || activeLineIndex >= lines.length) return undefined;

    const line = lines[activeLineIndex];
    const lineId = line.id ?? `idx-${activeLineIndex}`;
    const segs = splitIntoSegments(line.text);

    const clearTimers = () => {
      if (startTimerRef.current) { clearTimeout(startTimerRef.current); startTimerRef.current = null; }
      if (gapTimerRef.current) { clearTimeout(gapTimerRef.current); gapTimerRef.current = null; }
      if (segTimerRef.current) { clearInterval(segTimerRef.current); segTimerRef.current = null; }
      // 締切の記録も一緒に捨てる。残すと前の行の残り時間で張り直されうる
      gapStateRef.current = null;
    };

    // 行が切り替わったときだけ、前の行の音声とタイマーを片付ける
    if (currentIdRef.current !== lineId) {
      clearTimers();
      // 前の行のリスナーを外し、鳴っていれば止める
      if (detachRef.current) { detachRef.current(); detachRef.current = null; }
      const player = getPlayer();
      if (!player.paused) player.pause();
      currentIdRef.current = lineId;
      setReady(false);
    }

    const isCurrent = () => currentIdRef.current === lineId;

    // 再開時に残り時間で張り直せるよう、締切を覚えておく
    const armGap = (ms) => {
      // 既存のタイマーは必ず捨ててから張る。
      // 上書きするだけだと古いタイマーが生き残り、行が二重に進んでセリフが飛ぶ
      if (gapTimerRef.current) clearTimeout(gapTimerRef.current);
      gapStateRef.current = { lineId, deadline: Date.now() + ms };
      gapTimerRef.current = setTimeout(() => {
        gapTimerRef.current = null;
        gapStateRef.current = null;
        if (isCurrent()) onLineCompleteRef.current();
      }, ms);
    };

    const advanceWithGap = () => {
      if (!isCurrent() || gapTimerRef.current) return;
      setSegmentIndex(segs.length - 1);
      armGap(LINE_GAP_MS);
    };

    // 音声が無い / 失敗したときは文字数から所要時間を見積もって進める
    const startTimerFallback = () => {
      if (!isCurrent() || fallbackStartedRef.current.has(lineId)) return;
      if (pausedRef.current) { pendingStartRef.current = lineId; return; }
      fallbackStartedRef.current.add(lineId);
      setReady(true);
      const duration = Math.max(3000, line.text.length * 180);
      if (segs.length > 1) {
        const interval = duration / segs.length;
        let idx = 0;
        segTimerRef.current = setInterval(() => {
          idx++;
          if (idx < segs.length) {
            setSegmentIndex(idx);
          } else {
            clearInterval(segTimerRef.current);
            segTimerRef.current = null;
          }
        }, interval);
      }
      armGap(duration + LINE_GAP_MS);
    };
    fallbackFnRef.current = startTimerFallback;

    // すでにこの行を鳴らし始めていれば、読み込み直さずそのまま続ける
    // （同じ行での再描画で頭から鳴り直さないための番人）
    if (loadedIdRef.current === lineId) {
      setReady(true);
      return undefined;
    }

    const audioUrl = audioMapRef.current?.[activeLineIndex];
    if (audioUrl) {
      // モバイルでは Audio 要素を作り直すと再生を拒否されるため、
      // 解錠済みの共有要素の src だけを差し替える（audioPlayer.js 参照）
      const audio = getPlayer();
      loadedIdRef.current = lineId;

      audio.pause();
      audio.muted = muted;
      audio.src = audioUrl;
      audio.currentTime = 0;

      const onPlaying = () => { if (isCurrent()) setReady(true); };
      const onTimeUpdate = () => {
        if (!isCurrent() || !audio.duration || segs.length <= 1) return;
        const progress = audio.currentTime / audio.duration;
        setSegmentIndex(Math.min(Math.floor(progress * segs.length), segs.length - 1));
      };
      const onEnded = () => advanceWithGap();
      const onError = () => startTimerFallback();

      audio.addEventListener('playing', onPlaying);
      audio.addEventListener('timeupdate', onTimeUpdate);
      audio.addEventListener('ended', onEnded);
      audio.addEventListener('error', onError);
      // 共有要素なので、行が変わるときに必ず外す。残すと前の行の処理が混ざる
      detachRef.current = () => {
        audio.removeEventListener('playing', onPlaying);
        audio.removeEventListener('timeupdate', onTimeUpdate);
        audio.removeEventListener('ended', onEnded);
        audio.removeEventListener('error', onError);
      };

      startTimerRef.current = setTimeout(() => {
        startTimerRef.current = null;
        // 行が変わっている場合は鳴らさない
        if (!isCurrent() || loadedIdRef.current !== lineId) return;
        if (pausedRef.current) { pendingStartRef.current = lineId; return; }
        audio.play().catch(startTimerFallback);
      }, LINE_START_DELAY_MS);

      return undefined;
    }

    // ここに来るのは、この行の音声が無い（合成に失敗した）とき。
    // 同じ行で再描画が起きても、タイマーを二重に張らない
    if (!fallbackStartedRef.current.has(lineId) && !startTimerRef.current) {
      startTimerRef.current = setTimeout(() => {
        startTimerRef.current = null;
        startTimerFallback();
      }, LINE_START_DELAY_MS);
    }

    return undefined;
  }, [activeLineIndex, lines, muted]);

  // ミュートの切り替えを、再生中の音声にも即座に反映する
  useEffect(() => {
    getPlayer().muted = muted;
  }, [muted]);

  // 一時停止 / 再開。
  // 音声を止めるだけでなく、次の行へ進むタイマーも止める（＝会話が進まない）。
  // 再開時は残り時間で張り直すので、止めた続きから進む。
  useEffect(() => {
    const lineId = currentIdRef.current;

    if (paused) {
      const audio = getPlayer();
      if (!audio.paused) audio.pause();

      if (gapTimerRef.current) {
        clearTimeout(gapTimerRef.current);
        gapTimerRef.current = null;
        if (gapStateRef.current) {
          gapStateRef.current.remaining = Math.max(
            0,
            gapStateRef.current.deadline - Date.now(),
          );
        }
      }
      if (segTimerRef.current) {
        clearInterval(segTimerRef.current);
        segTimerRef.current = null;
      }
      return;
    }

    // ── 再開 ──
    const audio = getPlayer();

    // プレイヤーは全行で使い回す1つの要素なので、
    // 「いま入っている音声がこの行のものか」を必ず確かめてから鳴らす。
    //
    // 音声合成に失敗した行には音声が無く、そのときプレイヤーには
    // 前の行の音声が鳴り終わった状態で residual に残っている。
    // 確かめずに play() すると、ended の要素は仕様上そのまま先頭へ巻き戻るため、
    // 前の行のセリフを丸ごともう一度喋ってしまう（テロップは今の行のまま1回）。
    const audioIsForThisLine = loadedIdRef.current != null && loadedIdRef.current === lineId;

    if (pendingStartRef.current != null && pendingStartRef.current === lineId) {
      // 一時停止中に始められなかった行を、ここで始める
      pendingStartRef.current = null;
      if (audioIsForThisLine && !audio.ended) {
        audio.play().catch(() => fallbackFnRef.current?.());
      } else {
        // この行の音声は無い。前の行を鳴らさず、文字送りで進める
        fallbackFnRef.current?.();
      }
    } else if (audioIsForThisLine && audio.paused && !audio.ended) {
      // 途中で止めた音声を、止めた続きから鳴らす
      audio.play().catch(() => undefined);
    }

    // 止めた時点の残り時間でタイマーを張り直す
    const state = gapStateRef.current;
    if (state && state.remaining != null && state.lineId === lineId) {
      const wait = state.remaining;
      state.remaining = null;
      state.deadline = Date.now() + wait;
      gapTimerRef.current = setTimeout(() => {
        gapTimerRef.current = null;
        gapStateRef.current = null;
        if (currentIdRef.current === lineId) onLineCompleteRef.current();
      }, wait);
    }
  }, [paused]);

  // 会話ブロックが変わったら再生済みの記録をリセットする
  useEffect(() => {
    fallbackStartedRef.current = new Set();
  }, [lines]);

  // アンマウント時は全部止める
  useEffect(() => () => {
    if (startTimerRef.current) clearTimeout(startTimerRef.current);
    if (gapTimerRef.current) clearTimeout(gapTimerRef.current);
    if (segTimerRef.current) clearInterval(segTimerRef.current);
    if (detachRef.current) detachRef.current();
    const player = getPlayer();
    if (!player.paused) player.pause();
    loadedIdRef.current = null;
    currentIdRef.current = null;
  }, []);

  if (!currentLine || !ready) return null;

  const charInfo = CHARACTER_MAP[currentLine.speaker] || {};
  const displayText = segments[segmentIndex] || currentLine.text;

  return (
    <div className="subtitle-overlay">
      {isFirstAppearance && (
        <div className="subtitle-name" key={`name-${currentLine.speaker}`}>
          {/* キャラの色は丸で示す（文字色は --ink 一択） */}
          <span className="dot" style={{ background: charInfo.color }} />
          {charInfo.displayName || currentLine.speaker}
        </div>
      )}
      <div className="subtitle-text" key={`${currentLine.speaker}-${activeLineIndex}-${segmentIndex}`}>
        {displayText}
      </div>
    </div>
  );
};

export default ConversationDisplay;
